import { useEffect, useState } from "react";
import { toast } from "sonner";
import { portalApi, portalError } from "./portalApi";
import { Btn, Empty } from "../components/UI";
import { Search, Star, Plus, Trash2, ShieldCheck } from "lucide-react";

export default function PortalPharmacies() {
  const [dir, setDir] = useState([]);
  const [mine, setMine] = useState([]);
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("directory"); // "directory" or "manual"
  const [manual, setManual] = useState({ name: "", phone: "", address: "", city: "", state: "", zip: "" });
  const [busy, setBusy] = useState(false);

  const loadMine = () => portalApi.get("/portal/pharmacies").then((r) => setMine(r.data)).catch((e) => toast.error(portalError(e)));

  useEffect(() => { loadMine(); }, []);
  useEffect(() => {
    if (mode !== "directory") return;
    const t = setTimeout(() => {
      portalApi.get(`/portal/pharmacy-directory${q ? `?q=${encodeURIComponent(q)}` : ""}`)
        .then((r) => setDir(r.data)).catch(() => setDir([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, mode]);

  const pick = async (entry, preferred) => {
    setBusy(true);
    try {
      await portalApi.post("/portal/pharmacies", { directory_id: entry.id, preferred });
      toast.success(`${entry.name} added to your account`);
      loadMine();
    } catch (err) { toast.error(portalError(err)); }
    finally { setBusy(false); }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    if (!manual.name.trim()) return toast.error("Please enter a pharmacy name");
    setBusy(true);
    try {
      await portalApi.post("/portal/pharmacies", { ...manual, preferred: true });
      toast.success("Sent to your care team for review");
      setManual({ name: "", phone: "", address: "", city: "", state: "", zip: "" });
      setMode("directory"); loadMine();
    } catch (err) { toast.error(portalError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name}?`)) return;
    try {
      await portalApi.delete(`/portal/pharmacies/${p.id}`);
      toast.success("Removed");
      loadMine();
    } catch (err) { toast.error(portalError(err)); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="portal-pharmacies">
      <div>
        <h1 className="font-heading text-lg font-bold text-[#3E2723]">My Pharmacies</h1>
        <div className="text-xs text-[#7B6157]">Pick a preferred pharmacy so refills are routed there. Your care team can see this list.</div>
      </div>

      <div className="rounded-2xl bg-white shadow-sm border border-[#EFEBE0] p-4">
        <div className="text-xs font-bold uppercase text-[#7B6157] mb-2">On file</div>
        {mine.length === 0 ? <Empty text="No pharmacy on file yet. Pick one below or add your own." /> : (
          <div className="space-y-2">
            {mine.map((p) => (
              <div key={p.id} data-testid={`portal-mine-pharmacy-${p.id}`} className="rounded-xl border border-[#EFEBE0] p-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-[#3E2723] truncate">{p.name}</span>
                    {p.preferred && <span className="rounded-full bg-[#4E342E] text-white text-[9px] font-bold px-2 py-0.5">Preferred</span>}
                    {p.status === "pending_staff_review" && <span className="rounded-full bg-[#FFF3E0] text-[#8D5A00] text-[9px] font-bold px-2 py-0.5">Awaiting Review</span>}
                  </div>
                  <div className="mt-0.5 text-[11px] text-[#7B6157]">{[p.address, p.city, p.state, p.zip].filter(Boolean).join(", ") || "No address"}</div>
                  {p.phone && <div className="text-[11px] text-[#7B6157] tabular-nums">Ph: {p.phone}</div>}
                </div>
                <button type="button" data-testid={`portal-remove-pharmacy-${p.id}`} onClick={() => remove(p)}
                  className="h-8 w-8 rounded-full text-[#C62828] hover:bg-[#FFEBEE] flex items-center justify-center"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-white shadow-sm border border-[#EFEBE0] p-4">
        <div className="flex items-center gap-1 rounded-lg bg-[#F1EEE7] p-1 mb-3">
          <button type="button" data-testid="portal-pharm-mode-directory" onClick={() => setMode("directory")}
            className={`flex-1 rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-wide ${mode === "directory" ? "bg-[#4E342E] text-white" : "text-[#7B6157] hover:text-[#4E342E]"}`}>
            Search Directory
          </button>
          <button type="button" data-testid="portal-pharm-mode-manual" onClick={() => setMode("manual")}
            className={`flex-1 rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-wide ${mode === "manual" ? "bg-[#4E342E] text-white" : "text-[#7B6157] hover:text-[#4E342E]"}`}>
            Enter Manually
          </button>
        </div>

        {mode === "directory" ? (
          <>
            <div className="relative mb-3">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B6157]" />
              <input data-testid="portal-pharm-search" type="text" placeholder="Search by name, city, state, or ZIP"
                value={q} onChange={(e) => setQ(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#E5DFD3] bg-white text-sm outline-none focus:border-[#8D6E63]" />
            </div>
            {dir.length === 0 ? <Empty text="No pharmacies match. Try a different search or use Enter Manually." /> : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                {dir.map((p) => (
                  <div key={p.id} data-testid={`portal-directory-pharmacy-${p.id}`} className="rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm text-[#3E2723]">{p.name}</div>
                        <div className="text-[11px] text-[#7B6157]">{[p.address, p.city, p.state, p.zip].filter(Boolean).join(", ")}</div>
                        {p.phone && <div className="text-[11px] text-[#7B6157] tabular-nums">Ph: {p.phone}{p.hours ? ` · ${p.hours}` : ""}</div>}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <Btn data-testid={`portal-pick-pref-${p.id}`} disabled={busy} onClick={() => pick(p, true)} className="text-[10px] px-2.5 py-1.5">
                          <Star size={11} /> Preferred
                        </Btn>
                        <button type="button" data-testid={`portal-pick-add-${p.id}`} disabled={busy} onClick={() => pick(p, false)}
                          className="text-[10px] text-[#7B6157] hover:text-[#4E342E] font-semibold flex items-center gap-1"><Plus size={10} /> Add</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <form onSubmit={submitManual} className="space-y-2" data-testid="portal-pharm-manual-form">
            <div className="text-[11px] text-[#7B6157] flex items-center gap-1"><ShieldCheck size={12} /> Manual entries go to your care team for a quick review before they become preferred.</div>
            <input data-testid="portal-manual-name" placeholder="Pharmacy name" value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-[#E5DFD3] bg-white text-sm outline-none focus:border-[#8D6E63]" required />
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="portal-manual-phone" placeholder="Phone" value={manual.phone} onChange={(e) => setManual({ ...manual, phone: e.target.value })}
                className="px-3 py-2 rounded-lg border border-[#E5DFD3] bg-white text-sm outline-none focus:border-[#8D6E63]" />
              <input data-testid="portal-manual-zip" placeholder="ZIP" value={manual.zip} onChange={(e) => setManual({ ...manual, zip: e.target.value })}
                className="px-3 py-2 rounded-lg border border-[#E5DFD3] bg-white text-sm outline-none focus:border-[#8D6E63]" />
            </div>
            <input data-testid="portal-manual-address" placeholder="Address" value={manual.address} onChange={(e) => setManual({ ...manual, address: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-[#E5DFD3] bg-white text-sm outline-none focus:border-[#8D6E63]" />
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="portal-manual-city" placeholder="City" value={manual.city} onChange={(e) => setManual({ ...manual, city: e.target.value })}
                className="px-3 py-2 rounded-lg border border-[#E5DFD3] bg-white text-sm outline-none focus:border-[#8D6E63]" />
              <input data-testid="portal-manual-state" placeholder="State" value={manual.state} onChange={(e) => setManual({ ...manual, state: e.target.value })}
                className="px-3 py-2 rounded-lg border border-[#E5DFD3] bg-white text-sm outline-none focus:border-[#8D6E63]" />
            </div>
            <Btn data-testid="portal-manual-submit" type="submit" disabled={busy} className="w-full justify-center">
              <Plus size={13} /> Send to Care Team
            </Btn>
          </form>
        )}
      </div>
    </div>
  );
}
